import MD5 from 'crypto-js/md5';
import { IApiEnv, ISendCodeParams } from './types';

const _getScrmPrefixUrl = (env: IApiEnv) => {
  switch (env) {
    case 'test':
      return 'https://scrm-m-te.pharmacyyf.com/scrm-open';
    default:
      return 'https://scrm-m.yifengx.com/scrm-open';
  }
};

const _getStaticShareBaseUrl = (env: IApiEnv) => {
  switch (env) {
    case 'test':
      return 'https://yf-test-oss.yifengx.com/share';
    default:
      return 'https://static.pharmacyyf.com/share';
  }
};

const _loadScript = (url: string) =>
  new Promise((resolve) => {
    const script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = () => {
      resolve(true);
    };
    script.onerror = () => {
      resolve(false);
    };
    document.head.appendChild(script);
  });

const _sendSmsCodeByFetch = ({ env, ...restOptions }: ISendCodeParams & { sign: string; timestamp: number }): Promise<{ code: number; message: string }> => {
  return fetch(`${_getScrmPrefixUrl(env)}/api/MessageService/sendCusSmsCode`, {
    method: 'POST',
    body: JSON.stringify({
      smsQuery: {
        ...restOptions,
      },
    }),
  })
    .then((response) => {
      return response.json().then((result: { code: number; message?: string } | null) => {
        if (result?.code === 0) {
          return {
            code: 0,
            message: 'success',
          };
        }
        return {
          code: 500,
          message: result?.message ?? '获取短信验证码失败',
        };
      });
    })
    .catch(() => {
      return {
        code: 500,
        message: '获取短信验证码失败',
      };
    });
};

const _sendSmsCode = ({ env, ...restData }: ISendCodeParams): Promise<{ code: number; message: string }> => {
  const signRes = (window as any).__YF_WASM_SMS_GET_SIGN?.({
    ...restData,
    env,
  }) as { sign: string; timestamp: number };
  return _sendSmsCodeByFetch({
    env,
    ...restData,
    ...signRes,
  });
};

const _getSignSecretKey = ({ env, source }: Pick<ISendCodeParams, 'env' | 'source'>) => {
  if (env === 'test') {
    switch (source) {
      case 'SCRM_D':
        return '8D5A3A42EF19C3BEB2F81031ACFCF641A29C1F09';
      case 'SCRM_JZT_D':
        return '02FE8F589135C49B8933EFD1A41709C783AFBC6C';
      case 'DJYY':
        return '7E25E91F05C071E20FDC92FBF9F9882B188767EA';
      default:
        return '';
    }
  } else {
    switch (source) {
      case 'SCRM_D':
        return '8D5B3DBD748BB3AAB967C1079CE1540D14A87A1F';
      case 'SCRM_JZT_D':
        return '031800A8A8072E8AAB39D30D7678A259EB04AB1C';
      case 'DJYY':
        return 'C2FF02C67AEAF1D7C6AD9675607B708CA2EB6AF8';
      default:
        return '';
    }
  }
};

const _getSmsSignByJs = (options: ISendCodeParams) => {
  const secretKey = _getSignSecretKey({ source: options.source, env: options.env });
  const mobileFirstPart = options.mobile.slice(0, 3);
  const mobileLastPart = options.mobile.slice(-8);
  const timestamp = +new Date();
  const sign = MD5([mobileFirstPart, secretKey, timestamp, mobileLastPart].join('')).toString();
  return { sign, timestamp };
};

// 发送验证码
export const sendSmsCode = async ({ env, wasmUrl, wasmExecUrl, ...restData }: ISendCodeParams): Promise<{ code: number; message: string }> => {
  if (!!(window as any).__YF_WASM_SMS_GET_SIGN) {
    return _sendSmsCode({ env, ...restData });
  }

  try {
    if (!(window as any).Go) {
      const wasmExecRes = await _loadScript(!!wasmExecUrl ? wasmExecUrl : `${_getStaticShareBaseUrl(env)}/wasm/wasm_exec.min.js`);
      if (!wasmExecRes) {
        throw new Error('load wasm_exec fail');
      }
    }
    /**
     *  使用 WebAssembly.instantiateStreaming 更合适，但不知为何此项目使用 WebAssembly.instantiateStreaming 总是失败
     */
    const go = new (window as any).Go();
    const response = await fetch(!!wasmUrl ? wasmUrl : `${_getStaticShareBaseUrl(env)}/wasm/sms.v1.wasm`);
    const bytes = await response.arrayBuffer();
    const result = await WebAssembly.instantiate(bytes, go.importObject);
    go.run(result.instance);
    return _sendSmsCode({ env, ...restData });
  } catch (error) {
    const realSource = restData.source === 'DJYY' ? restData.source : ([restData.source, 'D'].join('_') as 'SCRM_D' | 'SCRM_JZT_D');
    const _signResult = _getSmsSignByJs({ env, ...restData, source: realSource });
    return _sendSmsCodeByFetch({ env, ...restData, ..._signResult, source: realSource });
  }
};
